import { inject, Injectable } from '@angular/core';
import { CanActivate, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { AuthService, Role, SessionService } from '@core/auth';

export function canActivateWithRoles(roles: Role[]) {
  return () => {
    const guard = inject(RoleBasedAccessGuard);
    return guard.canActivateWithRoles(roles);
  };
}

export function canNotActivateWithRoles(roles: Role[]) {
  return () => {
    const guard = inject(RoleBasedAccessGuard);
    return guard.canNotActivateWithRoles(roles);
  };
}
export function canActivateAdministration() {
  return () => {
    const guard = inject(RoleBasedAccessGuard);
    return guard.canActivateAdministration();
  };
}

export function canActivateAudit() {
  return () => {
    const guard = inject(RoleBasedAccessGuard);
    return guard.canActivateAudit();
  };
}
export function isAuthenticatedGuard() {
  return () => {
    const guard = inject(RoleBasedAccessGuard);
    return guard.isAuthenticatedGuard();
  };
}
@Injectable({
  providedIn: 'root',
})
export class RoleBasedAccessGuard implements CanActivate {
  #session = inject(SessionService);
  #router = inject(Router);
  #authService = inject(AuthService);

  /**
   * Role-based redirect configuration, ordered by priority (highest first).
   * Higher-privilege roles must be checked before lower-privilege roles
   * to prevent redirect loops (e.g., Root+Mitarbeiter redirecting to /my
   * which blocks CMF_HRP_TECH_ADMIN).
   *
   * Uses getter functions instead of boolean snapshots to ensure
   * role checks are evaluated dynamically at navigation time.
   */
  private readonly roleRedirects: Record<string, { check: () => boolean; redirect: string }> = {
    isSuperAdministrator: {
      check: () => this.#session.isSuperAdministrator,
      redirect: '/planning',
    },
    isAdministrator: {
      check: () => this.#session.isAdministrator,
      redirect: '/planning',
    },
    isPlaner: {
      check: () => this.#session.isPlanner,
      redirect: '/planning',
    },
    isInfo: {
      check: () => this.#session.isInfo,
      redirect: '/kalender/group',
    },
    isBetriebsrat: {
      check: () => this.#session.isBetriebsrat,
      redirect: '/audit',
    },
    isEmployee: {
      check: () => this.#session.isMitarbeiter,
      redirect: '/my',
    },
    default: { check: () => false, redirect: '/messages' },
  };

  canActivateWithRoles(roles: Role[]): boolean {
    // Wenn eine gespeicherte Ziel-URL existiert, überspringe den Redirect
    // damit der Benutzer zur gewünschten Seite navigieren kann
    if (this.#authService.hasIntendedUrl()) {
      return true;
    }

    if (roles?.every((role) => this.#session?.roles?.includes(role))) {
      return true;
    } else {
      const isInfo = this.#session?.isInfo;

      // Hier kann man individuelle Startseiten bzw. Weiterleitungen für verschiedene Rollen definieren
      if (isInfo) {
        this.#router.navigate([this.roleRedirects.isInfo.redirect]);
      } else {
        this.#router.navigate([this.roleRedirects.default.redirect]);
      }

      return false;
    }
  }

  canNotActivateWithRoles(roles: Role[]): boolean {
    // Wenn eine gespeicherte Ziel-URL existiert, überspringe den Redirect
    // damit der Benutzer zur gewünschten Seite navigieren kann
    if (this.#authService.hasIntendedUrl()) {
      return true;
    }

    if (roles.some((role) => this.#session?.roles?.includes(role))) {
      for (const { check, redirect } of Object.values(this.roleRedirects)) {
        if (check()) {
          this.#router.navigate([redirect]);
          return false;
        }
      }
      this.#router.navigate([this.roleRedirects.default.redirect]);
      return false;
    }
    return true;
  }

  isAuthenticatedGuard() {
    if (!this.#session.isLoggedIn) {
      this.#session.login();
      return false;
    }
    if (!this.#session.isAuthorized) {
      inject(Router).navigate(['/auth-error'], {
        queryParams: { code: 'unauthorized' },
      });
      return false;
    }
    return true;
  }
  canActivateAdministration() {
    return this.#session?.isAdministrator || this.#session?.isSuperAdministrator || false;
  }

  /**
   * The audit pages query contributor/values/query, which users with only
   * employee/info-level roles may not call (403) — they are redirected to
   * their start page instead. Combined roles (e.g. works council members who
   * are also employees) keep access.
   */
  canActivateAudit(): boolean {
    const session = this.#session;
    const onlyEmployeeOrInfo =
      (session?.isMitarbeiter || session?.isInfo) &&
      !session?.isBetriebsrat &&
      !session?.isPlanner &&
      !session?.isAdministrator &&
      !session?.isSuperAdministrator;
    if (!onlyEmployeeOrInfo) {
      return true;
    }
    const redirect = session?.isInfo
      ? this.roleRedirects.isInfo.redirect
      : this.roleRedirects.isEmployee.redirect;
    this.#router.navigate([redirect]);
    return false;
  }

  canActivate(): boolean | Observable<boolean> | Promise<boolean> {
    return false;
  }
}
